/**
 * TaskReaderPane - a task attachment, read.
 *
 * IT IS THE BODY, NOT THE HOST. It has been both: a docked pane of the task
 * surface, and - now, and before that - the app's global side panel, which is
 * where a document opens everywhere else in the product. Reading a file that
 * slides in from the right on one page and docks as a column on another is
 * two answers to one question, so it is the drawer. See
 * `TaskDetail.handleOpenAttachment` and the `taskAttachmentReader` case in
 * `GlobalSidePanel`.
 *
 * Nothing here knows which host it is in: it takes content and an onClose and
 * renders the document. That is what made the move a routing change rather
 * than a rewrite, and it is worth keeping true.
 *
 * It renders the same `ReaderComponent` it always has - the blob is still
 * fetched by `useGlobalStreaming` in the page, so nothing about the download
 * path has changed.
 *
 * WHAT IS THIS? It showed a title and a document and nothing else: no
 * size, no type, no uploader, no date. The same Document info block the
 * previous phase settled on for the matter reader sits above the document
 * here, built from the shared `InfoRow` / `MetaTile` primitives and fed by the
 * `Attachment` the list already normalized - no extra request. COLLAPSED BY
 * DEFAULT, because the pane exists to be read in.
 */

import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PiCaretDownLight, PiInfoLight, PiUserLight } from 'react-icons/pi';
import clsx from 'clsx';
import ReaderComponent from '../../shared/TailwindComponents/ReaderComponent';
import type { TaskReaderContent } from './TaskSurfaceContext';
import Spinner from '../../shared/TailwindComponents/Spinner';
import { InfoRow, Well } from '../../shared/brand/Field';
import { fileTypeLabel, formatFileSize } from '../../shared/attachments';
import { formatAbsoluteTime, formatRelativeTime } from '../../shared/feed/feedTime';

interface Props {
  content: TaskReaderContent | null;
  onClose: () => void;
}

const TaskReaderPane: React.FC<Props> = ({ content, onClose }) => {
  const { t } = useTranslation();
  const [infoOpen, setInfoOpen] = useState(false);

  if (!content) {
    return (
      <div className="h-full flex items-center justify-center p-6">
        <p className="text-xs text-fg-muted text-center">
          {t('Open an attachment to read it here.')}
        </p>
      </div>
    );
  }

  if (content.loading || !content.citation) {
    return (
      <div className="h-full flex flex-col items-center justify-center gap-2 p-6">
        <Spinner size="sm" />
        <p className="text-xs text-fg-muted text-center truncate max-w-full">{content.title}</p>
      </div>
    );
  }

  const attachment = content.attachment;
  /* Anything the payload cannot answer is dropped rather than rendered as a
     dash - a block of "—" is worse than a shorter block. */
  const facts: { label: string; value: string }[] = attachment
    ? [
        { label: t('Uploaded by'), value: attachment.uploadedBy || '' },
        {
          label: t('Uploaded'),
          value: attachment.uploadedAt ? formatAbsoluteTime(attachment.uploadedAt) : '',
        },
        { label: t('Size'), value: formatFileSize(attachment.size) },
        { label: t('Type'), value: fileTypeLabel(attachment.name, attachment.mime) },
      ].filter((fact) => !!fact.value)
    : [];

  return (
    <div className="flex flex-col h-full min-h-0">
      {facts.length > 0 && (
        <div className="flex-shrink-0 px-3 pt-3">
          <button
            type="button"
            onClick={() => setInfoOpen((open) => !open)}
            aria-expanded={infoOpen}
            className="flex w-full items-center gap-2 rounded-md px-1 py-1 text-[11px] font-medium text-fg-muted hover:text-fg-body transition-colors duration-200 ease-slide"
          >
            <PiInfoLight className="h-3.5 w-3.5 flex-shrink-0" />
            <span>{t('Document info')}</span>
            {/* The one fact worth having without expanding. */}
            {!infoOpen && attachment?.uploadedBy && (
              <span className="ms-auto flex min-w-0 items-center gap-1 truncate">
                <PiUserLight className="h-3.5 w-3.5 flex-shrink-0" />
                <span className="truncate">{attachment.uploadedBy}</span>
              </span>
            )}
            <PiCaretDownLight
              className={clsx(
                'h-3.5 w-3.5 flex-shrink-0 transition-transform duration-200 ease-slide',
                infoOpen ? 'rotate-180' : 'rotate-0',
                !infoOpen && attachment?.uploadedBy ? '' : 'ms-auto'
              )}
            />
          </button>
          {infoOpen && (
            <Well className="mt-2 space-y-2">
              {facts.map((fact) => (
                <InfoRow key={fact.label} label={fact.label} value={fact.value} />
              ))}
              {attachment?.uploadedAt && (
                <p className="text-[11px] text-fg-muted">
                  {formatRelativeTime(attachment.uploadedAt, t)}
                </p>
              )}
            </Well>
          )}
        </div>
      )}

      <div className="flex-1 min-h-0">
        <ReaderComponent
          citation={content.citation}
          matterId={content.taskUuid}
          sourceType="file"
          userAccessLevel={content.userAccessLevel}
          onClose={onClose}
        />
      </div>
    </div>
  );
};

export default TaskReaderPane;
